/**
 * EmailThreadParticipantsStack — the header's compact participant surface.
 *
 * Sub-header: up to MAX_CHIPS borderless (avatar + name) participants, then a
 * "+N" overflow indicator. Clicking opens a popover that lists every
 * participant as a clean full-width row (avatar + name + email) — no truncated
 * pills — each opening the participant's slideout when it resolves to a CRM
 * entity. Avatars are initials, matching the sub-header (not the badge
 * system's generated identicons).
 */

import { useCallback, useMemo } from 'react';
import {
  Button as AriaButton,
  Dialog,
  DialogTrigger,
} from 'react-aria-components';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { Avatar } from '@/lib/base/avatar/avatar';
import { Popover } from '@/lib/base/overlays/popover';
import type { Participant } from '@/lib/crm/components/ParticipantPillList';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { getEntityPrimaryEmail } from '@/lib/crm/store/crmObjectStore';
import type { CrmObject } from '@/types/crm';

import { getDisplayName } from '../../utils/getDisplayName';
import {
  humanizeEmailLocalPart,
  initialsForLabel,
  viewerAvatarInitials,
} from '../timeline/threadMessages';
import { useThreadParticipants } from './useThreadParticipants';

/** Named chips shown in the sub-header before the +N overflow indicator. */
const MAX_CHIPS = 3;

/** The name a participant reads as, mirroring ParticipantPillList's rules. */
function participantLabel(p: Participant): string {
  if (p.name) {
    return p.name;
  }
  if (p.object) {
    return getDisplayName(p.object);
  }
  if (p.email) {
    return humanizeEmailLocalPart(p.email) ?? p.email;
  }
  return '?';
}

interface ParticipantRowData {
  key: string;
  label: string;
  initials: string;
  email: string | undefined;
  /** Entity id when resolved to a CRM object — enables click-to-slideout. */
  entityId: string | undefined;
}

interface ParticipantRowProps {
  row: ParticipantRowData;
  onOpen: (entityId: string) => void;
}

/** One row in the participants popover; clickable only when resolved. */
function ParticipantRow({ row, onOpen }: ParticipantRowProps) {
  const handlePress = useCallback(() => {
    if (row.entityId) {
      onOpen(row.entityId);
    }
  }, [onOpen, row.entityId]);

  const inner = (
    <>
      <Avatar size='sm' initials={row.initials} alt={row.label} />
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm font-medium text-[var(--color-text-primary)]'>
          {row.label}
        </span>
        {row.email && (
          <span className='block truncate text-xs text-[var(--color-text-tertiary)]'>
            {row.email}
          </span>
        )}
      </span>
    </>
  );

  if (!row.entityId) {
    return (
      <div className='flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5'>
        {inner}
      </div>
    );
  }
  return (
    <AriaButton
      onPress={handlePress}
      className='flex w-full items-center gap-2.5 rounded-md px-1.5 py-1.5 text-left outline-none hover:bg-[var(--color-bg-secondary)] focus-visible:bg-[var(--color-bg-secondary)]'
    >
      {inner}
    </AriaButton>
  );
}

interface EmailThreadParticipantsStackProps {
  object: CrmObject;
}

export function EmailThreadParticipantsStack({
  object,
}: EmailThreadParticipantsStackProps) {
  const participants = useThreadParticipants(object);
  const { user } = useAuthInfo();
  const openSlideout = useOpenSlideoutOptional();

  const rows = useMemo<ParticipantRowData[]>(
    () =>
      participants.map(p => {
        const label = participantLabel(p);
        const initials =
          label === 'You'
            ? (viewerAvatarInitials(user?.display, user?.email) ??
              initialsForLabel(label))
            : initialsForLabel(label);
        const email =
          p.email ?? (p.object ? getEntityPrimaryEmail(p.object) : undefined);
        return {
          key: p.key,
          label,
          initials,
          email,
          entityId: p.object?.entity_id,
        };
      }),
    [participants, user?.display, user?.email]
  );

  const handleOpen = useCallback(
    (entityId: string) => {
      openSlideout?.(entityId);
    },
    [openSlideout]
  );

  if (participants.length === 0) {
    return null;
  }

  const count = participants.length;
  const countLabel = `${count} ${count === 1 ? 'participant' : 'participants'}`;
  const visibleChips = rows.slice(0, MAX_CHIPS);
  const overflowCount = rows.length - MAX_CHIPS;

  return (
    <DialogTrigger>
      <AriaButton
        className='inline-flex w-fit cursor-pointer items-center gap-3 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-600)] focus-visible:ring-offset-1'
        aria-label={`${countLabel}; click to list`}
      >
        {visibleChips.map(({ key, label, initials }) => (
          <span key={key} className='inline-flex items-center gap-1.5'>
            <Avatar size='xs' initials={initials} alt={label} />
            <span className='max-w-[10rem] truncate text-sm text-[var(--color-text-primary)]'>
              {label}
            </span>
          </span>
        ))}
        {overflowCount > 0 && (
          <span className='text-sm text-[var(--color-text-tertiary)]'>
            +{overflowCount}
          </span>
        )}
      </AriaButton>
      {/* Fixed width + start-alignment: the trigger is a small chip, and
          inheriting `w-(--trigger-width)` would squeeze the list to a sliver. */}
      <Popover size='md' placement='bottom start' className='w-80!'>
        <Dialog>
          <div className='p-2'>
            <p className='px-1.5 pb-1.5 pt-1 text-sm font-medium text-[var(--color-text-primary)]'>
              Participants
            </p>
            <ul className='flex flex-col' data-testid='participant-list'>
              {rows.map(row => (
                <li key={row.key}>
                  <ParticipantRow row={row} onOpen={handleOpen} />
                </li>
              ))}
            </ul>
          </div>
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
